import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../context/AuthContext.jsx';
import AuthSidePanel from '../components/AuthSidePanel.jsx';

export default function Signup() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ fullName: '', email: '', password: '', signupCode: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  }

  function nextStep(e) {
    e.preventDefault();
    if (!form.fullName.trim() || form.fullName.length < 2) {
      setError('Please enter a valid full name (at least 2 characters).');
      return;
    }
    if (!form.email.trim() || !form.email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setStep(2);
  }

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/register', form);
      login(data.token, data.staff);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-pampa-50">
        <div className="w-full max-w-md animate-slideDown">
          <Link to="/" className="inline-flex items-center space-x-2 mb-8">
            <span className="text-2xl font-semibold text-grey-900 tracking-wider">Sharre Support</span>
          </Link>

          <h1 className="text-3xl font-semibold text-grey-900 mb-2 tracking-tight">Join our team</h1>
          <p className="text-grey-600 mb-8 font-medium tracking-wide">
            {step === 1 ? 'Apply to become a support staff member' : 'Complete your application'}
          </p>

          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === 1 ? 'bg-grey-900 text-white' : 'bg-grey-200 text-grey-600'
                }`}
              >
                1
              </div>
              <div className={`w-12 h-0.5 ${step === 2 ? 'bg-grey-900' : 'bg-grey-200'}`}></div>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === 2 ? 'bg-grey-900 text-white' : 'bg-grey-200 text-grey-600'
                }`}
              >
                2
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 font-medium">{error}</p>
            </div>
          )}

          {step === 1 && (
            <form onSubmit={nextStep} className="space-y-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-grey-700 mb-2 tracking-wide">
                  Full name
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  required
                  minLength={2}
                  maxLength={100}
                  className="w-full px-4 py-3 border border-pampa-300 rounded-lg focus:ring-2 focus:ring-grey-600 focus:border-transparent transition bg-white font-medium"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-grey-700 mb-2 tracking-wide">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-pampa-300 rounded-lg focus:ring-2 focus:ring-grey-600 focus:border-transparent transition bg-white font-medium"
                  placeholder="you@example.com"
                />
              </div>

              <button
                type="submit"
                className="w-full px-6 py-3 bg-grey-900 text-white font-medium rounded-lg hover:bg-grey-800 transition tracking-wide"
              >
                Continue
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submit} className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-grey-700 mb-2 tracking-wide">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="w-full px-4 py-3 pr-12 border border-pampa-300 rounded-lg focus:ring-2 focus:ring-grey-600 focus:border-transparent transition bg-white font-medium"
                    placeholder="********"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-grey-500 hover:text-grey-700 transition"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    )}
                  </button>
                </div>
                <p className="mt-1 text-xs text-grey-500 font-medium">At least 8 characters</p>
              </div>

              <div>
                <label htmlFor="signupCode" className="block text-sm font-medium text-grey-700 mb-2 tracking-wide">
                  Support access code
                </label>
                <input
                  type="text"
                  id="signupCode"
                  name="signupCode"
                  value={form.signupCode}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-pampa-300 rounded-lg focus:ring-2 focus:ring-grey-600 focus:border-transparent transition bg-white font-medium"
                  placeholder="Enter access code"
                />
                <p className="mt-1 text-xs text-grey-500 font-medium">Contact your administrator for the access code.</p>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-1/3 px-6 py-3 bg-grey-200 text-grey-700 font-medium rounded-lg hover:bg-grey-300 transition tracking-wide"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-2/3 px-6 py-3 bg-grey-900 text-white font-medium rounded-lg hover:bg-grey-800 transition disabled:opacity-50 disabled:cursor-not-allowed tracking-wide"
                >
                  {loading ? 'Creating account...' : 'Create account'}
                </button>
              </div>
            </form>
          )}

          <p className="mt-8 text-center text-sm text-grey-600 font-medium tracking-wide">
            Already have an account?{' '}
            <Link to="/login" className="text-grey-900 hover:text-grey-700 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <AuthSidePanel subtitle="Support Desk" />
    </div>
  );
}
